
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Users, BarChart, Calendar, MessageSquare, ClipboardList, Database, Phone } from 'lucide-react';
import FAQSection from '@/components/ui/FAQSection';
import PricingCard from '@/components/ui/PricingCard';

const Software = () => {
  const pricingFeatures = [
    "Unlimited contact management",
    "One phone line for calls and SMS",
    "Unlimited storage",
    "Project management",
    "Email and SMS campaigns",
    "Social media scheduler for Facebook",
    "SERP reports",
    "Call recordings",
    "Automations",
    "Meeting links",
    "Forms",
    "Custom data fields",
    "2,500 emails per month",
    "250 texts per month",
    "250 voicemail drops per month",
  ];

  const faqs = [
    {
      question: "What type of hosting do you provide?",
      answer: "We provide enterprise-grade hosting powered by Amazon Web Services (AWS), ensuring top-tier speed, reliability, and security. Our hosting includes a global Content Delivery Network (CDN) to optimize site load times, load balancing for traffic distribution, and SSL encryption for secure data transfer. With AWS's scalable infrastructure, your site is designed to handle high traffic volumes while maintaining consistent uptime and performance."
    },
    {
      question: "Is it GDPR and HIPAA compliant?",
      answer: "Yes, we are both GDPR and HIPAA compliant. For HIPAA compliance, you will need to contact us to activate your account and provide the necessary documentation. There is no additional fee for HIPAA-compliant setup."
    },
    {
      question: "Can I receive emails within the platform?",
      answer: "No, our platform was designed for outbound email newsletters, individual emails, and bulk emails. The system is optimized for your marketing and customer outreach needs rather than as an email inbox replacement."
    },
    {
      question: "Can I access the platform on mobile devices?",
      answer: "Yes, you can access the platform on mobile through your phone or tablet's internet browser. While we don't have native mobile apps, most key information is accessible on mobile devices. However, the platform is primarily designed for desktop use to provide the best possible experience with all features fully accessible."
    },
    {
      question: "How many users can I add to my account?",
      answer: "The base plan includes one user. Additional users can be added for $15 per month per user. This allows you to scale your team access as your business grows."
    },
    {
      question: "Do you offer training on how to use the software?",
      answer: "Yes, we provide comprehensive onboarding and training sessions to help you get the most out of our platform. We also offer ongoing support and resources to ensure your success."
    }
  ];

  return (
    <PageLayout 
      title="Software Solutions | Astrem" 
      description="Custom software, CRM systems, and all-in-one platforms designed to streamline operations and enhance business efficiency."
      ogImage="public/lovable-uploads/908e0364-dc0e-4b62-a951-5740a58e2736.png"
    >
      <PageHero
        title="CRM & Marketing Platform"
        subtitle="One powerful platform for your entire business"
        description="Streamline your operations, enhance customer relationships, and drive growth with our comprehensive CRM and marketing software solution."
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Laptop_displaying_a_CRM_interface.jpg"
        ctaText="Get Started"
        ctaLink="/contact"
      />

      {/* Main Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                One Platform for Your Entire Business
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Our all-in-one CRM and marketing platform combines powerful tools that help you manage contacts, automate processes, and grow your business efficiently.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/CRM_Dashboard.png" 
                alt="CRM Dashboard" 
                className="rounded-xl shadow-xl w-full"
              />
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Customer Relationship Management</h3>
                    <p className="text-astrem-gray-600">
                      Store unlimited contacts, track interactions, and manage your customer relationships all in one place with our intuitive CRM system.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Marketing Automation</h3>
                    <p className="text-astrem-gray-600">
                      Create targeted email and SMS campaigns that nurture leads and drive conversions, with automation that saves you time and effort.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Social Media Management</h3>
                    <p className="text-astrem-gray-600">
                      Schedule and publish Facebook posts directly from the platform, keeping your social presence active without switching between tools.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Integrated Communications</h3>
                    <p className="text-astrem-gray-600">
                      Make calls, send SMS, and manage voicemail drops from one unified platform with our Plivo integration for seamless customer outreach.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Core Features Cards */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Features That Power Your Business Growth
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Discover the tools and capabilities that will transform how you manage your business and engage with customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Customer Intelligence Hub</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Centralize your customer data and gain insights into behaviors, preferences, and engagement patterns to personalize your approach.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Unlimited contact storage with custom fields</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Track customer interaction history</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Segment audiences for targeted marketing</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Project Management</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Organize your team's work, manage tasks, and track project milestones with our intuitive project management tools.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Create and assign tasks to team members</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Set deadlines and receive reminders</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Visualize project progress with Kanban boards</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <BarChart className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Marketing Analytics</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Gain insights into website traffic, campaign performance, and referral sources to optimize your marketing strategy.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Track website visitor activity</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Monitor search engine rankings (SERP)</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Analyze referral link performance</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Data Into Action Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200} className="order-2 lg:order-1">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Turn Data Into Action
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Our platform helps you collect, analyze, and act on customer data to make informed business decisions and drive growth.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Review recorded phone calls from sales teams to improve customer service
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Track customer website activity to understand their interests
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Monitor search engine rankings (SERP) to enhance your SEO strategy
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Analyze campaign performance and adjust strategies in real-time
                    </p>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
            
            <ScrollReveal className="order-1 lg:order-2">
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/analytics_data.png" 
                alt="Analytics Dashboard" 
                className="rounded-xl shadow-xl w-full"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Automate Business Processes */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Automate Your Business Processes
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Increase efficiency by automating repetitive tasks, communications, and workflows to focus on what matters most.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Marketing Automation</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Create automated email and SMS sequences that nurture leads and keep customers engaged without manual intervention.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Set up drip campaigns based on customer actions</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Schedule bulk email and SMS campaigns</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Integrated phone lines and SMS messaging with Plivo</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <ClipboardList className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Workflow Automation</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Build custom workflows that automatically assign tasks, send notifications, and update records based on triggers.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Create IF/THEN automation rules</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Automate follow-up tasks and reminders</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Streamline approval processes</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Database className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">Lead Capture & Management</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Automatically capture leads from your website and social media, then route them to the right team members.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Website form integration with data pixel</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Lead scoring and qualification</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-astrem-gray-700">Automated lead assignment rules</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Centralize Operations Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/projects-crm.jpg" 
                alt="Centralized Operations" 
                className="rounded-xl shadow-xl w-full"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Centralize Your Business Operations
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Bring all your business functions together in one platform to eliminate silos and improve collaboration.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Manage all customer data and interactions in one place
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Seamlessly move between marketing, sales, and project management
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Enable cross-team collaboration with shared calendars and tasks
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Maintain a unified view of all business activities and performance
                    </p>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Build Strong Customer Relationships */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200} className="order-2 lg:order-1">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Build Stronger Customer Relationships
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Deliver personalized experiences at scale with tools designed to help you understand and engage your customers.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Track all customer interactions across channels
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Set up personalized communication sequences
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Create targeted customer segments for precise marketing
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Implement automated follow-ups at the right moment
                    </p>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
            
            <ScrollReveal className="order-1 lg:order-2">
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/sales_feed_view_crm.png" 
                alt="Customer Relationships" 
                className="rounded-xl shadow-xl w-full"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Social Media Scheduler Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/social_scheduler.jpg" 
                alt="Social Media Scheduler" 
                className="rounded-xl shadow-xl w-full"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Schedule Your Facebook Posts
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Maintain a consistent social media presence by scheduling Facebook posts in advance, all from within our platform.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Plan and schedule content for optimal posting times
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Upload and manage media for your posts
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      View your content calendar at a glance
                    </p>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-astrem-gray-700">
                      Maintain a consistent brand presence without daily manual posting
                    </p>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Get started with our powerful CRM and marketing platform with a pricing plan that works for your business.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <ScrollReveal delay={100}>
              <div className="bg-white rounded-xl border border-astrem-gray-200 shadow-xl overflow-hidden h-full">
                <div className="p-6 md:p-8">
                  <h3 className="text-xl font-medium mb-2">Initial Setup Fee</h3>
                  <div className="mb-2">
                    <span className="text-3xl font-semibold">$950</span>
                    <span className="text-astrem-gray-500 ml-2">one-time</span>
                  </div>
                  <p className="text-astrem-gray-600 mb-6">
                    Professional configuration and setup of your CRM and marketing platform.
                  </p>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-astrem-gray-700">Custom fields configuration</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-astrem-gray-700">Contact upload and organization</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-astrem-gray-700">Data pixel setup for lead capture</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-astrem-gray-700">Portal branding and white labeling</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-astrem-gray-700">Basic automations for email and SMS</span>
                    </li>
                  </ul>
                  
                  <Button asChild className="w-full mt-4">
                    <Link to="/contact">Contact Us</Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white rounded-xl border border-primary shadow-xl overflow-hidden h-full">
                <div className="bg-primary py-2 text-white text-center">
                  <span className="text-sm font-medium">Most Popular</span>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="text-xl font-medium mb-2">Software Subscription</h3>
                  <div className="mb-2">
                    <span className="text-3xl font-semibold">$50</span>
                    <span className="text-astrem-gray-500 ml-2">/month</span>
                  </div>
                  <p className="text-astrem-gray-600 mb-6">
                    All the essential tools you need to manage and grow your business.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {pricingFeatures.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <div className="mr-3 mt-0.5 rounded-full p-1 bg-green-100 text-green-600">
                          <Check className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-astrem-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button asChild className="w-full mt-4">
                    <Link to="/contact">Get Started</Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={300}>
            <div className="mt-12 bg-white p-8 rounded-xl border border-astrem-gray-200 shadow-md">
              <div className="flex items-start md:items-center flex-col md:flex-row gap-6">
                <div className="flex-grow">
                  <h3 className="text-xl font-medium mb-2">Need more? Custom plans available</h3>
                  <p className="text-astrem-gray-600">
                    If you need higher limits, additional users, or custom features, we offer flexible plans to meet your specific business requirements.
                  </p>
                </div>
                <Button asChild className="whitespace-nowrap">
                  <Link to="/contact">Contact for Custom Quote</Link>
                </Button>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={400}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl border border-astrem-gray-200 shadow-sm">
                <div className="flex items-start">
                  <div className="mr-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">10DLC Registration</h3>
                    <p className="text-astrem-gray-600 mb-2">
                      Consultation, assistance, and facilitation for 10DLC registration.
                    </p>
                    <p className="text-sm text-astrem-gray-500">$750 one-time fee</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl border border-astrem-gray-200 shadow-sm">
                <div className="flex items-start">
                  <div className="mr-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Database className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">API Connections</h3>
                    <p className="text-astrem-gray-600 mb-2">
                      Custom API integrations with other systems and platforms.
                    </p>
                    <p className="text-sm text-astrem-gray-500">Contact for custom pricing</p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Frequently Asked Questions"
        description="Find answers to common questions about our CRM and marketing platform."
        faqs={faqs}
      />

      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Ready to Transform Your Business?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-300 mb-8">
                Get in touch today to learn how our CRM and marketing platform can help you streamline operations and drive growth.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">Contact</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Software;
